/* eslint-disable react/jsx-props-no-spreading */
import {
  DateOfBirthInput,
  Input,
  PhoneNumberInput
} from '@library/components/inputs';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { DEFAULT_PROFILE_DETAILS } from '@bo-constants/member/members';
import { Form } from '@library/components/forms';
import { MEMBER_ACCOUNT_INFO_INPUT_CLASSES } from '@bo-constants/member/memberAccountInformation';
import { MemberAddressAutocompleteFormField } from '@bo-components/members/forms/MemberAddressAutocompleteFormField';
import { PrizepicksSwitch } from '@library/components/switches';
import { memo } from 'react';

interface MemberAccountFormLabelProps {
  className?: string;
  labelName: string;
  required?: boolean;
}

const MemberAccountFormLabel = ({
  className = '',
  labelName,
  required = false
}: MemberAccountFormLabelProps) => (
  <Form.Label className="text-soClean-180 text-xs">
    <span className={className}>{labelName}</span>
    {required && <span className="text-roses px-1">*</span>}
  </Form.Label>
);

export const MemberAccountInformationFormFields = memo(() => {
  const form = useFormContext<FieldValues>();

  const {
    ERROR_CLASS,
    FULL_WIDTH,
    HALF_WIDTH,
    INPUT_CLASS,
    READ_ONLY_INPUT_CLASS
  } = MEMBER_ACCOUNT_INFO_INPUT_CLASSES;

  return (
    <div className="flex w-full flex-wrap gap-x-4 gap-y-2">
      <Form.Field
        control={form.control}
        name="firstName"
        render={({ field }) => (
          <Form.Item className={HALF_WIDTH}>
            <MemberAccountFormLabel labelName="First name" required />
            <Form.Control>
              <Input className={INPUT_CLASS} {...field} />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="lastName"
        render={({ field }) => (
          <Form.Item className={HALF_WIDTH}>
            <MemberAccountFormLabel labelName="Last name" required />
            <Form.Control>
              <Input className={INPUT_CLASS} {...field} />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="email"
        render={({ field }) => (
          <Form.Item className={FULL_WIDTH}>
            <MemberAccountFormLabel labelName="Email" required />
            <Form.Control>
              <Input className={INPUT_CLASS} {...field} />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="username"
        render={({ field }) => (
          <Form.Item className="w-[calc(75%-0.5rem)]">
            <MemberAccountFormLabel labelName="Display name" />
            <Form.Control>
              <Input
                className={
                  field.value === DEFAULT_PROFILE_DETAILS.username
                    ? READ_ONLY_INPUT_CLASS
                    : INPUT_CLASS
                }
                disabled={field.value === DEFAULT_PROFILE_DETAILS.username}
                {...field}
              />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="verified"
        render={({ field }) => (
          <Form.Item className="w-[calc(25%-0.5rem)] text-right">
            <MemberAccountFormLabel labelName="Verified" />
            <Form.Control>
              <div>
                <PrizepicksSwitch
                  active={!!field.value}
                  handleChange={field.onChange}
                />
              </div>
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="phoneNumber"
        render={({ field }) => (
          <Form.Item className={HALF_WIDTH}>
            <MemberAccountFormLabel labelName="Phone number" />
            <Form.Control>
              <PhoneNumberInput
                className="h-[38px]"
                id="phoneNumber"
                onValueChange={field.onChange}
                {...field}
              />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="dateOfBirth"
        render={({ field }) => (
          <Form.Item className={HALF_WIDTH}>
            <MemberAccountFormLabel labelName="Date of birth" required />
            <Form.Control>
              <DateOfBirthInput
                className="h-[38px]"
                id="dateOfBirth"
                onValueChange={field.onChange}
                {...field}
              />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="address"
        render={({ field }) => (
          <Form.Item className={FULL_WIDTH}>
            <MemberAccountFormLabel labelName="Street address" required />
            <Form.Control>
              <MemberAddressAutocompleteFormField
                fieldProps={field}
                formMethods={form}
              />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="city"
        render={({ field }) => (
          <Form.Item className={HALF_WIDTH}>
            <MemberAccountFormLabel labelName="City" required />
            <Form.Control>
              <Input className={READ_ONLY_INPUT_CLASS} disabled {...field} />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="state"
        render={({ field }) => (
          <Form.Item className={HALF_WIDTH}>
            <MemberAccountFormLabel labelName="State code" required />
            <Form.Control>
              <Input className={READ_ONLY_INPUT_CLASS} disabled {...field} />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="postalCode"
        render={({ field }) => (
          <Form.Item className={HALF_WIDTH}>
            <MemberAccountFormLabel labelName="Zip code" required />
            <Form.Control>
              <Input className={READ_ONLY_INPUT_CLASS} disabled {...field} />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="inviteCode"
        render={({ field }) => (
          <Form.Item className={HALF_WIDTH}>
            <MemberAccountFormLabel labelName="Invite code" />
            <Form.Control>
              <Input className={INPUT_CLASS} {...field} />
            </Form.Control>
            <Form.Message className={ERROR_CLASS} />
          </Form.Item>
        )}
      />
    </div>
  );
});
